/**
 * Quote Type Definitions - Workshop 4 Aligned
 *
 * Simplified quote types aligned with Victorian Sliders actual workflow
 * NO customer PII (GDPR protected - stays with installer)
 */

import { IssueType } from './issue.types'

/**
 * Quote status throughout lifecycle - Client Requirements
 */
export enum QuoteStatus {
  /** Quote sent, awaiting customer decision - shown in QUOTES tab */
  Pending = 'pending',
  /** Live quote sent to customer, awaiting response */
  Quoted = 'quoted',
  /** Quote was sent to customer */
  Sent = 'sent',
  /** Customer converted quote to order - shown in ORDERS tab */
  Ordered = 'ordered',
  /** Payment received from customer */
  PaymentReceived = 'payment_received',
  /** Order completed and delivered */
  Completed = 'completed',
  /** Internal sales team followed up */
  FollowedUp = 'followed_up',
  /** Quote has problems requiring resolution */
  Issue = 'issue',
}

/**
 * Follow-up status (separate from order status)
 */
export enum FollowUpStatus {
  /** Quote sent, no contact attempted */
  NotContacted = 'not_contacted',
  /** Attempted contact, no response */
  Attempted = 'attempted',
  /** Contact made with customer */
  ContactMade = 'contact_made',
  /** Currently in discussion with customer */
  InDiscussion = 'in_discussion',
  /** Follow-up completed */
  FollowedUp = 'followed_up',
}

/**
 * Foil type (simplified)
 */
export enum FoilType {
  /** Standard finish */
  Standard = 'standard',
  /** Foil finish */
  Foil = 'foil',
}

/**
 * Follow-up action record
 */
export interface FollowUpAction {
  /** Action ID */
  id: string

  /** Quote this action relates to */
  quoteId: string

  /** Type of follow-up action */
  actionType: 'phone_call' | 'email' | 'meeting' | 'customer_response'

  /** User who performed action */
  performedBy: string
  performedByName: string

  /** When action was performed */
  actionDate: Date

  /** Notes about the action */
  notes: string
}

/**
 * Complete quote object (Workshop 4 aligned)
 */
export interface Quote {
  /** Unique quote identifier */
  id: string

  /** Human-readable reference (VS-YYYY-NNNNN format) */
  referenceNumber: string

  /** Installer company that created quote */
  installerCompanyName: string
  installerCompanyId: string

  /** UK Region (Area 1, Scotland, etc.) */
  region: string

  /** Customer's internal project reference (NO personal data) */
  projectReferenceCode?: string

  /** Quote financial details */
  quoteDate: Date
  totalValue: number
  numberOfWindows: number
  numberOfFrames: number
  foilType: FoilType

  /** Status tracking */
  status: QuoteStatus
  followUpStatus: FollowUpStatus

  /** Issue tracking (if status = Issue) */
  issueType?: IssueType
  issueDescription?: string
  issueReported?: Date
  issueResolvedBy?: string
  issueResolutionNotes?: string

  /** Assignment to VS sales team */
  salesPersonId: string
  salesPersonName: string

  /** Timestamps */
  createdAt: Date
  updatedAt: Date
  lastFollowUpDate?: Date
  convertedToOrderDate?: Date
}

/**
 * Simplified quote summary for lists
 */
export interface QuoteSummary {
  id: string
  referenceNumber: string
  installerCompanyName: string
  region: string
  totalValue: number
  numberOfFrames: number
  foilType: FoilType
  status: QuoteStatus
  followUpStatus: FollowUpStatus
  quoteDate: Date
}

/**
 * Quote filter options
 */
export interface QuoteFilter {
  /** Filter by status */
  status?: QuoteStatus[]

  /** Filter by follow-up status */
  followUpStatus?: FollowUpStatus[]

  /** Filter by region */
  region?: string[]

  /** Filter by installer company */
  installerCompanyId?: string

  /** Filter by foil type */
  foilType?: FoilType[]

  /** Filter by date range */
  dateFrom?: Date
  dateTo?: Date

  /** Filter by value range */
  valueMin?: number
  valueMax?: number

  /** Search term (reference number, project code) */
  search?: string

  /** Pagination */
  page?: number
  limit?: number

  /** Sorting */
  sortBy?: 'quoteDate' | 'totalValue' | 'referenceNumber' | 'numberOfFrames'
  sortOrder?: 'asc' | 'desc'
}

/**
 * Paginated quote list response
 */
export interface PaginatedQuotes {
  quotes: Quote[]
  total: number
  page: number
  limit: number
  totalPages: number
}

/**
 * Quote statistics
 */
export interface QuoteStatistics {
  /** Total quotes */
  total: number

  /** By status */
  byStatus: Record<QuoteStatus, number>

  /** By region */
  byRegion: Record<string, number>

  /** By foil type */
  byFoilType: Record<FoilType, number>

  /** Financial metrics */
  totalValue: number
  averageValue: number
  totalFrames: number
  averageFramesPerQuote: number

  /** Conversion metrics */
  conversionRate: number

  /** This month */
  thisMonth: number
}

export default Quote
